
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  ctraderTokenRefreshService, 
  CTraderConnection, 
  RefreshResponse, 
  BalanceSyncResponse 
} from '@/services/ctrader-token-refresh-service';
import { useToast } from '@/hooks/use-toast';

export function useCTraderManagement() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSyncingBalances, setIsSyncingBalances] = useState(false);
  const [refreshingConnections, setRefreshingConnections] = useState<Set<string>>(new Set());
  const [syncingBalanceConnections, setSyncingBalanceConnections] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: connections, isLoading, refetch } = useQuery({
    queryKey: ['admin-ctrader-connections'],
    queryFn: ctraderTokenRefreshService.getAllCTraderConnections,
  });

  const handleRefreshResult = (response: RefreshResponse) => {
    const { summary, results } = response;
    
    // Show summary toast
    if (summary.failed === 0) {
      toast({
        title: "Token Refresh Successful",
        description: `Successfully refreshed ${summary.successful} token(s)`,
      });
    } else if (summary.successful === 0) {
      toast({
        title: "Token Refresh Failed", 
        description: `Failed to refresh ${summary.failed} token(s)`,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Token Refresh Completed",
        description: `${summary.successful} successful, ${summary.failed} failed`,
        variant: summary.failed > summary.successful ? "destructive" : "default"
      });
    }

    // Show individual error toasts for failed refreshes
    results.filter(r => !r.success).forEach(result => {
      toast({
        title: `Failed: ${result.apiName}`,
        description: result.error || "Unknown error",
        variant: "destructive"
      });
    });

    // Refresh the connections list
    refetch();
  };

  const handleBalanceSyncResult = (response: BalanceSyncResponse) => {
    const { summary, results } = response;
    
    // Show summary toast
    if (summary.failed === 0) {
      toast({
        title: "Balance Sync Successful",
        description: `Successfully synced ${summary.successful} account(s)`,
      });
    } else if (summary.successful === 0) {
      toast({
        title: "Balance Sync Failed", 
        description: `Failed to sync ${summary.failed} account(s)`,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Balance Sync Completed",
        description: `${summary.successful} successful, ${summary.failed} failed`,
        variant: summary.failed > summary.successful ? "destructive" : "default"
      });
    }

    // Show individual error toasts for failed syncs
    results.filter(r => !r.success).forEach(result => {
      toast({
        title: `Sync Failed: ${result.apiName}`,
        description: result.error || "Unknown error",
        variant: "destructive"
      });
    });

    // Refresh the connections list
    refetch();
  };

  const refreshAllTokens = async () => {
    setIsRefreshing(true);
    try {
      const response = await ctraderTokenRefreshService.refreshAllTokens();
      handleRefreshResult(response);
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshSingleToken = async (connectionId: string) => {
    setRefreshingConnections(prev => new Set(prev).add(connectionId));
    try {
      const response = await ctraderTokenRefreshService.refreshSingleToken(connectionId);
      handleRefreshResult(response);
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setRefreshingConnections(prev => {
        const next = new Set(prev);
        next.delete(connectionId);
        return next;
      });
    }
  };

  const refreshMultipleTokens = async (connectionIds: string[]) => {
    connectionIds.forEach(id => 
      setRefreshingConnections(prev => new Set(prev).add(id))
    );
    try {
      const response = await ctraderTokenRefreshService.refreshMultipleTokens(connectionIds);
      handleRefreshResult(response);
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      connectionIds.forEach(id => 
        setRefreshingConnections(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        })
      );
    }
  };

  const syncAllBalances = async () => {
    setIsSyncingBalances(true);
    try {
      const response = await ctraderTokenRefreshService.syncAccountBalances();
      handleBalanceSyncResult(response);
    } catch (error) {
      toast({
        title: "Balance Sync Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSyncingBalances(false);
    }
  };

  const syncSingleBalance = async (connectionId: string) => {
    setSyncingBalanceConnections(prev => new Set(prev).add(connectionId));
    try {
      const response = await ctraderTokenRefreshService.syncSingleAccountBalance(connectionId);
      handleBalanceSyncResult(response);
    } catch (error) {
      toast({
        title: "Balance Sync Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      setSyncingBalanceConnections(prev => {
        const next = new Set(prev);
        next.delete(connectionId);
        return next;
      });
    }
  };

  const syncMultipleBalances = async (connectionIds: string[]) => {
    connectionIds.forEach(id => 
      setSyncingBalanceConnections(prev => new Set(prev).add(id))
    );
    try {
      const response = await ctraderTokenRefreshService.syncMultipleAccountBalances(connectionIds);
      handleBalanceSyncResult(response);
    } catch (error) {
      toast({
        title: "Balance Sync Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive"
      });
    } finally {
      connectionIds.forEach(id => 
        setSyncingBalanceConnections(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        })
      );
    }
  };

  return {
    connections: connections || [],
    isLoading,
    isRefreshing,
    isSyncingBalances,
    refreshingConnections,
    syncingBalanceConnections,
    refreshAllTokens,
    refreshSingleToken,
    refreshMultipleTokens,
    syncAllBalances,
    syncSingleBalance,
    syncMultipleBalances,
    refetch
  };
}
